import { computed } from 'vue'
import { useAsyncData, useNuxtApp } from '#app'
import { useMainStore } from '~/composables/store'
import { getCurrentServerKey } from '~/utils/globalData'
import { readAustraliaPcNavigation, resolveAustraliaNavigation } from '../utils/navigation'

function getCachedPcNavigation(key, app, { cause } = {}) {
  if (cause === 'refresh:manual' || cause === 'refresh:hook') return
  const cached = app.payload.data[key] ?? app.static.data[key]
  return cached?.configured === true && Array.isArray(cached.items) ? cached : undefined
}

export function useAustraliaNavigation() {
  const store = useMainStore()
  const { $customFetch, $nowCurrentServer } = useNuxtApp()
  const shopId = computed(() => store.getDealerShopId($nowCurrentServer.value)
    || store.getServerShopId($nowCurrentServer.value))
  // SSR receives the bare Host while the browser uses location.origin. Both
  // must address the same payload entry during hydration.
  const cacheKey = computed(() => `australia-pc-navigation:${getCurrentServerKey($nowCurrentServer.value)}:${shopId.value}`)
  const { data: pcNavigation } = useAsyncData(cacheKey, async () => {
    if (!shopId.value) return { configured: false, items: [] }
    try {
      const response = await $customFetch('/frontServer/tabBar/list', {
        method: 'POST', timeout: 5000, retry: 0,
        body: { shopId: shopId.value, showType: '1', tree: false, pageNo: 0, pageSize: 1000 },
      })
      return readAustraliaPcNavigation(response)
    } catch {
      // A menu service failure must not make the existing storefront disappear.
      return { configured: false, items: [] }
    }
  }, {
    // A non-null empty object makes Nuxt consider a missing hydration entry
    // loaded successfully, preventing the initial browser request entirely.
    default: () => null, dedupe: 'defer',
    // Protected CMS content remounts the header after hydration. Reuse its
    // host/shop-scoped SSR menu beyond hydration instead of discarding it and
    // depending on a second browser request. Explicit refresh still re-fetches.
    getCachedData: getCachedPcNavigation,
  })

  return computed(() => resolveAustraliaNavigation(
    pcNavigation.value,
    store.$state.navigationList || [],
  ))
}
